/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { v4 } from "uuid";
import { Button, Spacer, Text, theme, Tooltip } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { EditLinks } from "~/components/BasicCustomerInfo/Edit/EditLinks";
import {
  BasicInfoEditModeType,
  BasicInfoLoadingType,
  Customer,
  Link,
  LinkItem,
} from "~/components/BasicCustomerInfo/types";
import { validateLink } from "~/components/BasicCustomerInfo/validation";
import { EditButton } from "~/components/InlineEdit";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import {
  DetailsLinkTitle,
  StyledOutsideLink,
} from "~/components/Styled/Common";
import { Divider } from "~/components/Styled/Divider";

type BasicInfoLinksProps = {
  name: string;
  items: LinkItem[];
  customer: Customer;
  onSubmit: (values: Customer) => Promise<void>;
  isLoading: BasicInfoLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<BasicInfoLoadingType>>;
  isEditMode: BasicInfoEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<BasicInfoEditModeType>>;
  canEdit?: boolean;
};

const EmptyLabel = styled.span`
  color: ${theme.color.text.text02};
`;

const StyledButton = styled(Button)`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: ${theme.spacing.spacing06};
`;

export const StyledMaxWidthWrapper = styled(FlexContainer)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding-bottom: 0.5rem;
`;

function NamedLinks({
  name,
  items,
  customer,
  onSubmit,
  isLoading,
  setIsLoading,
  isEditMode,
  setIsEditMode,
  canEdit = true,
}: BasicInfoLinksProps) {
  const [links, setLinks] = React.useState<Link[]>([]);

  const isReady = links.every((link) => validateLink(link).length === 0);

  React.useEffect(() => {
    const mapped = items?.map((link: LinkItem) => {
      return { id: v4(), name: link.name, desc: link.desc, url: link.url };
    });
    items && setLinks(mapped);
  }, [items, isEditMode.links]);

  const methods = useForm();

  const onEdit = async () => {
    setIsLoading((prevState) => ({ ...prevState, links: true }));
    const filteredId = links.map((el) => {
      return { name: el.name, desc: el.desc, url: el.url };
    });

    const organizeValues = {
      ...customer,
      info: { ...customer.info, links: filteredId },
    };

    await onSubmit(organizeValues);
  };

  const setEditMode = (value: boolean) => {
    setIsEditMode((prevState) => ({ ...prevState, links: value }));
  };

  return (
    <Divider>
      <Text isTitle tag="div" weight="medium" mb="1rem">
        {name}
        {canEdit && (
          <span css={{ position: "relative", top: "1px" }}>
            <EditButton onClick={() => setEditMode(true)} />
          </span>
        )}
      </Text>

      <FlexContainer data-cy={name}>
        <StyledMaxWidthWrapper direction="column" align="flex-start">
          {items?.length ? (
            items?.map((item, idx) => (
              <StyledMaxWidthWrapper key={idx}>
                <DetailsLinkTitle key={item.desc} data-cy="link">
                  {item.desc || "-"}
                </DetailsLinkTitle>
                <Tooltip caption={item.url}>
                  <StyledOutsideLink href={item.url} data-cy="link">
                    {item.name || item.url}
                  </StyledOutsideLink>
                </Tooltip>
              </StyledMaxWidthWrapper>
            ))
          ) : (
            <EmptyLabel>No links</EmptyLabel>
          )}
        </StyledMaxWidthWrapper>
      </FlexContainer>
      <Spacer height="1.5rem" />

      <SidebarForm
        title={name}
        open={isEditMode.links || false}
        setOpen={setEditMode}
        formMethods={methods}
        submit={onEdit}
      >
        <EditLinks links={links} setLinks={setLinks} />

        <StyledButton
          icon="plus"
          size="md"
          severity="medium"
          type="button"
          disabled={!isReady}
          onClick={() => {
            setLinks([...links, { id: v4(), name: "", desc: "", url: "" }]);
          }}
          data-cy="add-link"
        >
          Add New Link
        </StyledButton>

        <SidebarFormButtons
          onCancel={() => setEditMode(false)}
          submitLoading={isLoading["links"]}
          submitEnabled={isReady}
        />
      </SidebarForm>
    </Divider>
  );
}

export { NamedLinks };
